import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { FilterPreferences } from '../../services/api';
import { useListLayoutPreferences } from '../../hooks/useListLayoutPreferences';
import { useFilterPreferences } from '../../hooks/useFilterPreferences';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { getDurationPresetRange, DurationPreset } from '../dashboard/utils/dateHelpers';
import ListLayoutSettingsModal from '../../components/ListLayoutSettingsModal';
import ListLayoutSettingsButton from '../../components/ListLayoutSettingsButton';
import RefreshButton from '../../components/RefreshButton';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import TableColumnFilter from '../../components/TableColumnFilter';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { trackChangeOperation } from '../../utils/appInsights';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { isTicketSearchInputValid } from '../../utils/searchTerms';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import EmailTicketModal from '../../components/EmailTicketModal';
import GenericExportButton from '../../components/GenericExportButton';
import { ExportConfigs } from '../../services/genericExportService';

interface Change {
  id?: string;
  _id?: string;
  number: string;
  shortDescription: string;
  description?: string;
  priority: string;
  state: string;
  changeState?: string;
  category: string;
  ticketType: string;
  caseType: string;
  relatedRecordsCount?: number;
  canEdit?: boolean;
  plannedStartDate?: string;
  plannedEndDate?: string;
  User_Case_assignedToIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  Customer?: {
    id: string;
    accountName: string;
    accountShortName: string;
    emailAddress: string;
  };
  Engagement?: {
    id: string;
    name: string;
    status: string;
  };
  Group_Case_assignmentGroupIdToGroup?: {
    id: string;
    name: string;
    description?: string;
  };
  ParentCase?: {
    id: string;
    number: string;
    shortDescription: string;
    ticketType: string;
  };
  CaseActivity?: {
    id: string;
    activityType: string;
    metadata?: any;
    performedAt: string;
    User: {
      firstName: string;
      lastName: string;
      email: string;
    };
  }[];
  createdAt: string;
  updatedAt: string;
}

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  // Legacy support
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200',
  3: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  4: 'bg-blue-100 text-blue-800 border-blue-200',
  5: 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityLabels: Record<string, string> = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning',
  // Legacy support
  1: 'Critical',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Planning'
};

const stateIcons = {
  new: <DocumentTextIcon className="h-5 w-5 text-gray-500" />,
  assess: <MagnifyingGlassIcon className="h-5 w-5 text-blue-500" />,
  authorize: <ShieldCheckIcon className="h-5 w-5 text-yellow-500" />,
  scheduled: <ClockIcon className="h-5 w-5 text-purple-500" />,
  implement: <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />,
  review: <DocumentTextIcon className="h-5 w-5 text-indigo-500" />,
  closed: <CheckIcon className="h-5 w-5 text-green-500" />,
  cancelled: <XMarkIcon className="h-5 w-5 text-red-500" />
};

const stateLabels = {
  new: 'New',
  assess: 'Assess',
  authorize: 'Authorize',
  scheduled: 'Scheduled',
  implement: 'Implement',
  review: 'Review',
  closed: 'Closed',
  cancelled: 'Cancelled'
};

// Function to get the most recent approval/rejection actions
const getRecentActions = (activities?: Change['CaseActivity']) => {
  if (!activities || activities.length === 0) return [];
  
  return activities
    .filter(activity => 
      activity.activityType === 'change_approved' || 
      activity.activityType === 'change_rejected' ||
      activity.activityType === 'status_reverted'
    )
    .slice(0, 2); // Show only the 2 most recent actions
};

// Function to format activity for display
const formatActivityAction = (activity: NonNullable<Change['CaseActivity']>[0]) => {
  const user = activity.User;
  const userName = `${user.firstName} ${user.lastName}`.trim() || user.email;
  const date = new Date(activity.performedAt).toLocaleDateString();

  switch (activity.activityType) {
    case 'change_approved':
      return {
        text: `Approved by ${userName}`,
        color: 'text-green-600',
        date
      };
    case 'change_rejected':
      return {
        text: `Rejected by ${userName}`,
        color: 'text-red-600',
        date
      };
    case 'status_reverted':
      return {
        text: `Status reverted to assess`,
        color: 'text-orange-600',
        date
      };
    default:
      return {
        text: activity.activityType.replace(/_/g, ' '),
        color: 'text-gray-600',
        date
      };
  }
};

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Options for multi-select filters
const stateOptions = [
  { value: 'new', label: 'New' },
  { value: 'assess', label: 'Assess' },
  { value: 'authorize', label: 'Authorize' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'implement', label: 'Implement' },
  { value: 'review', label: 'Review' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions = [
  { value: 'critical_1', label: 'Critical' },
  { value: 'high_2', label: 'High' },
  { value: 'moderate_3', label: 'Moderate' },
  { value: 'low_4', label: 'Low' },
  { value: 'planning_5', label: 'Planning' },
];

const timeRangeOptions = [
  { value: '5', label: 'Last 5 mins' },
  { value: '15', label: 'Last 15 mins' },
  { value: '30', label: 'Last 30 mins' },
  { value: '60', label: 'Last 1 hour' },
  { value: '120', label: 'Last 2 hours' },
  { value: '480', label: 'Last 8 hours' },
  { value: '1440', label: 'Last 24 hours' },
  { value: 'preset:this_week', label: 'This Week' },
  { value: 'preset:last_week', label: 'Last Week' },
  { value: 'preset:this_month', label: 'This Month' },
  { value: 'preset:last_month', label: 'Last Month' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const CHANGE_LIST_URL_FILTER_KEYS = [
  'search',
  'number',
  'desc',
  'state',
  'priority',
  'time',
  'customer',
  'engagement',
  'alert',
  'groups',
  'assignedToMe',
  'assignedTo',
  'unassigned',
  'peer',
  'cab',
  'cust',
  'createdBy',
  'plannedStart',
  'plannedEnd',
] as const;

function isDefaultChangeListUrl(sp: URLSearchParams): boolean {
  const page = sp.get('page');
  if (page && page !== '1') return false;
  const ps = sp.get('pageSize');
  if (ps && ps !== '25') return false;
  for (const k of CHANGE_LIST_URL_FILTER_KEYS) {
    const v = sp.get(k);
    if (v !== null && v !== '') return false;
  }
  return true;
}

function buildChangeListUrlUpdatesFromSaved(
  savedFilters: FilterPreferences
): Record<string, string | null | undefined> {
  const updates: Record<string, string | null | undefined> = {};
  if (savedFilters.state?.length) updates.state = savedFilters.state.join(',');
  if (savedFilters.priority?.length) updates.priority = savedFilters.priority.join(',');
  if (savedFilters.timeRange === '') {
    updates.time = 'all';
  } else if (savedFilters.timeRange && savedFilters.timeRange !== 'preset:this_month') {
    updates.time = savedFilters.timeRange;
  }
  if (savedFilters.customer) updates.customer = savedFilters.customer;
  if (savedFilters.engagement) {
    updates.engagement = savedFilters.engagement;
  }
  if (savedFilters.assignmentGroup) {
    updates.groups = savedFilters.assignmentGroup;
  }
  if (savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null) {
    updates.assignedToMe =
      savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false';
  }
  if (savedFilters.assignedTo) updates.assignedTo = savedFilters.assignedTo;
  if (savedFilters.unassigned) updates.unassigned = savedFilters.unassigned;
  return updates;
}

function serializeChangeTimeRangeForUrl(timeRange: string): string | null {
  if (!timeRange) return 'all';
  if (timeRange === 'preset:this_month') return null;
  return timeRange;
}

function buildChangeDateFilterExportFields(
  prefix: string,
  rangeValue: string,
  timezoneOffset: number
): Record<string, string> {
  if (!rangeValue) return {};
  if (rangeValue.startsWith('custom:')) {
    const [, dates] = rangeValue.split('custom:');
    const [from, to] = dates.split(',');
    return {
      ...(from ? { [`${prefix}From`]: from } : {}),
      ...(to ? { [`${prefix}To`]: to } : {}),
      timezoneOffset: String(timezoneOffset),
    };
  }
  if (rangeValue.startsWith('preset:')) {
    const presetName = rangeValue.replace('preset:', '') as DurationPreset;
    const range = getDurationPresetRange(presetName);
    return {
      ...(range.from ? { [`${prefix}From`]: range.from } : {}),
      ...(range.to ? { [`${prefix}To`]: range.to } : {}),
      timezoneOffset: String(timezoneOffset),
    };
  }
  return { [`${prefix}Range`]: rangeValue };
}

function appendChangeDateFilterParams(
  params: URLSearchParams,
  prefix: string,
  rangeValue: string,
  timezoneOffset: number
): void {
  if (!rangeValue) return;
  if (rangeValue.startsWith('custom:')) {
    const [, dates] = rangeValue.split('custom:');
    const [from, to] = dates.split(',');
    if (from) params.append(`${prefix}From`, from);
    if (to) params.append(`${prefix}To`, to);
    params.append('timezoneOffset', String(timezoneOffset));
  } else if (rangeValue.startsWith('preset:')) {
    const presetName = rangeValue.replace('preset:', '') as DurationPreset;
    const range = getDurationPresetRange(presetName);
    if (range.from) params.append(`${prefix}From`, range.from);
    if (range.to) params.append(`${prefix}To`, range.to);
    params.append('timezoneOffset', String(timezoneOffset));
  } else {
    params.append(`${prefix}Range`, rangeValue);
  }
}

function parseOptionalChangeTimeFilter(sp: URLSearchParams, key: string): string {
  const t = sp.get(key);
  if (!t || t === 'all') return '';
  return t;
}

const ChangeList: React.FC = () => {
  // Subscribe to real-time change events via SSE
  useTicketEvents({ ticketTypes: ['CHANGE'] });

  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/changes', LIST_FILTER_SESSION_KEYS.changes);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') ?? '');
  const appliedSearchTerm = searchParams.get('search') ?? '';
  useEffect(() => {
    setSearchTerm(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const filterState = useMemo(
    () => (searchParams.get('state') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterPriority = useMemo(
    () => (searchParams.get('priority') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterTimeRange = useMemo(() => {
    const t = searchParams.get('time');
    if (t === 'all') return '';
    if (t === null || t === '') return 'preset:this_month';
    return t;
  }, [searchParams]);

  const filterCustomer = searchParams.get('customer') ?? '';
  const filterEngagement = useMemo(
    () => (searchParams.get('engagement') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const appliedAlertNumber = searchParams.get('alert') ?? '';
  const [filterAlertNumber, setFilterAlertNumber] = useState(() => searchParams.get('alert') ?? '');
  useEffect(() => {
    setFilterAlertNumber(appliedAlertNumber);
  }, [appliedAlertNumber]);

  const filterPendingPeerReview = searchParams.get('peer') === '1';
  const filterPendingCAB = searchParams.get('cab') === '1';
  const filterPendingCustomer = searchParams.get('cust') === '1';

  const filterAssignmentGroup = useMemo(
    () => (searchParams.get('groups') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterAssignedToMe = searchParams.get('assignedToMe') ?? '';
  const filterAssignedTo = searchParams.get('assignedTo') ?? '';
  const filterUnassigned = searchParams.get('unassigned') ?? '';
  const filterNumber = searchParams.get('number') ?? '';
  const filterDescription = searchParams.get('desc') ?? '';
  const filterCreatedBy = searchParams.get('createdBy') ?? '';
  const filterPlannedStart = useMemo(
    () => parseOptionalChangeTimeFilter(searchParams, 'plannedStart'),
    [searchParams]
  );
  const filterPlannedEnd = useMemo(
    () => parseOptionalChangeTimeFilter(searchParams, 'plannedEnd'),
    [searchParams]
  );

  const effectiveSearch = appliedSearchTerm || filterNumber || filterDescription;

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const savedPrefsHydratedRef = useRef(false);

  // Get browser's timezone offset (in minutes from UTC)
  // This is used to ensure date filtering is consistent with user's local timezone
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // Filter preferences
  const {
    filters: savedFilters,
    saveFilters,
    isSaving: isFilterSaving,
    isFetched: isFilterPrefsFetched,
  } = useFilterPreferences('CHANGE');

  // Apply saved preferences to URL once when URL is still default (no explicit filters)
  useEffect(() => {
    if (filtersInitialized) return;
    if (user?.id && !isFilterPrefsFetched) return;

    if (!isDefaultChangeListUrl(searchParams)) {
      setFiltersInitialized(true);
      return;
    }

    if (savedPrefsHydratedRef.current) {
      setFiltersInitialized(true);
      return;
    }
    savedPrefsHydratedRef.current = true;

    const updates = buildChangeListUrlUpdatesFromSaved(savedFilters);
    if (Object.keys(updates).length > 0) {
      patchSearchParams(updates);
    }
    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    isFilterPrefsFetched,
    user?.id,
    searchParams,
    savedFilters,
    patchSearchParams,
  ]);

  // Check if current filters differ from saved
  const savedEngagement = savedFilters.engagement
    ? (typeof savedFilters.engagement === 'string' ? savedFilters.engagement.split(',') : savedFilters.engagement)
    : [];
  const savedAssignmentGroup = savedFilters.assignmentGroup
    ? (typeof savedFilters.assignmentGroup === 'string' ? savedFilters.assignmentGroup.split(',') : savedFilters.assignmentGroup)
    : [];
  // Convert saved assignedToMe to string for comparison ('' if not set, 'true' or 'false' if set)
  const savedAssignedToMe = savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null
    ? (savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false')
    : '';
  const hasUnsavedFilterChanges = filtersInitialized && (
    JSON.stringify(filterState.sort()) !== JSON.stringify((savedFilters.state || []).sort()) ||
    JSON.stringify(filterPriority.sort()) !== JSON.stringify((savedFilters.priority || []).sort()) ||
    filterTimeRange !== (savedFilters.timeRange || '') ||
    filterCustomer !== (savedFilters.customer || '') ||
    JSON.stringify([...filterEngagement].sort()) !== JSON.stringify([...savedEngagement].sort()) ||
    JSON.stringify([...filterAssignmentGroup].sort()) !== JSON.stringify([...savedAssignmentGroup].sort()) ||
    filterAssignedToMe !== savedAssignedToMe ||
    filterAssignedTo !== (savedFilters.assignedTo || '') ||
    filterUnassigned !== (savedFilters.unassigned || '')
  );

  const handleSaveFilters = async () => {
    await saveFilters({
      state: filterState,
      priority: filterPriority,
      timeRange: filterTimeRange || undefined,
      customer: filterCustomer || undefined,
      engagement: filterEngagement.length > 0 ? filterEngagement.join(',') : undefined,
      assignmentGroup: filterAssignmentGroup.length > 0 ? filterAssignmentGroup.join(',') : undefined,
      assignedToMe: filterAssignedToMe || undefined,
      assignedTo: filterAssignedTo || undefined,
      unassigned: filterUnassigned || undefined,
    });
  };


  // Email modal state
  const [emailModalState, setEmailModalState] = useState({
    isOpen: false,
    ticketId: '',
    ticketNumber: '',
    shortDescription: ''
  });

  const openEmailModal = (change: Change) => {
    setEmailModalState({
      isOpen: true,
      ticketId: change.id || change._id || '',
      ticketNumber: change.number,
      shortDescription: change.shortDescription
    });
  };

  // List layout settings
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const {
    columns: layoutColumns,
    visibleColumns,
    saveColumns,
    resetToDefault,
    isSaving: isLayoutSaving,
    isResetting: isLayoutResetting,
  } = useListLayoutPreferences('CHANGE');

  // Export filters - build from current filter state
  const exportFilters = useMemo(() => ({
    ...(effectiveSearch && { search: effectiveSearch }),
    ...(filterState.length > 0 && { changeState: filterState.join(',') }),
    ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
    ...(filterTimeRange && { timeRange: filterTimeRange }),
    ...(filterCustomer && { customerId: filterCustomer }),
    ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
    ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
    ...(filterPendingPeerReview && { pendingPeerReview: 'true' }),
    ...(filterPendingCAB && { pendingCAB: 'true' }),
    ...(filterPendingCustomer && { pendingCustomer: 'true' }),
    ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
    ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
    ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
    ...(filterUnassigned && { unassigned: filterUnassigned }),
    ...(filterCreatedBy && { createdById: filterCreatedBy }),
    ...buildChangeDateFilterExportFields('plannedStart', filterPlannedStart, timezoneOffset),
    ...buildChangeDateFilterExportFields('plannedEnd', filterPlannedEnd, timezoneOffset),
  }), [effectiveSearch, filterState, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterPendingPeerReview, filterPendingCAB, filterPendingCustomer, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterCreatedBy, filterPlannedStart, filterPlannedEnd, timezoneOffset]);

  // Export columns - build from visible columns. Customer Type is exported
  // alongside the Customer column.
  const exportColumns = useMemo(() => {
    return visibleColumns.flatMap(col => (
      col.key === 'customer'
        ? [
            { key: col.key, header: col.label || col.key },
            { key: 'customerType', header: 'Customer Type' },
          ]
        : [{ key: col.key, header: col.label || col.key }]
    ));
  }, [visibleColumns]);

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements for selected customer (use UI state for immediate dropdown update)
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', filterCustomer],
    queryFn: async () => {
      if (!filterCustomer) return { engagements: [] };
      const response = await api.get(`/engagements/dropdown?customerId=${filterCustomer}`);
      return response.data;
    },
    enabled: !!filterCustomer
  });

  // Fetch user's assignment groups for dropdown
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data || [];
    },
  });

  const mapDropdownUserToOption = (u: { id: string; name?: string; email?: string }) => ({
    value: u.id,
    label: u.name || u.email || u.id,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-for-filter'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown');
      return response.data;
    },
  });

  // Build filter sections for FilterDropdown
  const filterSections: FilterSection[] = useMemo(() => [
    {
      id: 'state',
      label: 'State',
      type: 'multi-select',
      options: stateOptions,
      placeholder: 'All States',
    },
    {
      id: 'priority',
      label: 'Priority',
      type: 'multi-select',
      options: priorityOptions,
      placeholder: 'All Priorities',
    },
    {
      id: 'timeRange',
      label: 'Time Range',
      type: 'time-range',
      options: timeRangeOptions,
      placeholder: 'All Time',
    },
    {
      id: 'customer',
      label: 'Customer',
      type: 'searchable-select',
      options: customersData?.customers?.map((c: any) => ({
        value: c.id,
        label: c.accountShortName || c.accountName,
      })) || [],
      placeholder: 'All Customers',
    },
    {
      id: 'engagement',
      label: 'Engagement',
      type: 'searchable-multi-select',
      options: engagementsData?.engagements?.map((e: any) => ({
        value: e.id,
        label: e.name,
      })) || [],
      placeholder: 'All Engagements',
      disabled: !filterCustomer,
      disabledMessage: 'Select a customer first',
    },
    {
      id: 'alertNumber',
      label: 'Alert Number',
      type: 'text',
      placeholder: 'e.g., ALT001, ALT002',
      helpText: 'Enter single or multiple alert numbers separated by commas',
    },
    {
      id: 'assignmentGroup',
      label: 'Assignment Group',
      type: 'searchable-multi-select',
      options: assignmentGroupsData?.map((g: { id: string; name: string }) => ({
        value: g.id,
        label: g.name,
      })) || [],
      placeholder: 'All Groups',
    },
    {
      id: 'assignedToMe',
      label: 'Assigned To Me',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      placeholder: 'All Assignments',
    },
    {
      id: 'assignedTo',
      label: 'Assigned To',
      type: 'searchable-select',
      options: usersData?.users?.map(mapDropdownUserToOption) || [],
      placeholder: 'All Users',
    },
    {
      id: 'unassigned',
      label: 'Show Unassigned',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
      ],
      placeholder: 'All',
    },
  ], [customersData, engagementsData, assignmentGroupsData, usersData, filterCustomer]);

  const customerOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountShortName?: string; accountName?: string }) => ({
        value: c.id,
        label: c.accountShortName || c.accountName || c.id,
      })) ?? [],
    [customersData]
  );

  const engagementOptions = useMemo(
    () =>
      engagementsData?.engagements?.map((e: { id: string; name: string }) => ({
        value: e.id,
        label: e.name,
      })) ?? [],
    [engagementsData]
  );

  const assignmentGroupOptions = useMemo(
    () =>
      assignmentGroupsData?.map((g: { id: string; name: string }) => ({
        value: g.id,
        label: g.name,
      })) ?? [],
    [assignmentGroupsData]
  );

  const userFilterOptions = useMemo(
    (): Array<{ value: string; label: string }> =>
      usersData?.users?.map(mapDropdownUserToOption) ?? [],
    [usersData]
  );

  const assignedToOptions = useMemo(
    () => [{ value: '__unassigned__', label: 'Unassigned' }, ...userFilterOptions],
    [userFilterOptions]
  );

  const handleAssignedToColumnFilter = useCallback(
    (value: string | string[]) => {
      const v = typeof value === 'string' ? value : '';
      if (v === '__unassigned__') {
        patchSearchParams({ assignedTo: null, unassigned: 'true', page: '1' });
      } else if (v) {
        patchSearchParams({ assignedTo: v, unassigned: null, page: '1' });
      } else {
        patchSearchParams({ assignedTo: null, unassigned: null, page: '1' });
      }
    },
    [patchSearchParams]
  );

  const assignedToColumnValue = filterUnassigned === 'true' ? '__unassigned__' : filterAssignedTo;

  const createdTimeRangeOptions = useMemo(
    () => [...timeRangeOptions, { value: 'all', label: 'All Time' }],
    []
  );

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by number (comma or space separated)..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'shortDescription':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDescription}
              placeholder="Filter by description..."
              onChange={(v) => {
                patchSearchParams({ desc: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={filterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({
                  customer: typeof v === 'string' && v ? v : null,
                  engagement: null,
                  page: '1',
                });
              }}
            />
          );
        case 'engagement':
          if (!filterCustomer) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={engagementOptions}
              value={filterEngagement}
              placeholder="All Engagements"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ engagement: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'priority':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={priorityOptions}
              value={filterPriority}
              placeholder="All Priorities"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ priority: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'state':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={stateOptions}
              value={filterState}
              placeholder="All States"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ state: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'assignedTo':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={assignedToOptions}
              value={assignedToColumnValue}
              placeholder="All Users"
              onChange={handleAssignedToColumnFilter}
            />
          );
        case 'assignmentGroup':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={assignmentGroupOptions}
              value={filterAssignmentGroup}
              placeholder="All Groups"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ groups: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'createdAt':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={createdTimeRangeOptions}
              value={filterTimeRange || 'all'}
              placeholder="All Time"
              active={searchParams.has('time')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                const timeParam = serializeChangeTimeRangeForUrl(val === 'all' ? '' : val);
                patchSearchParams({ time: timeParam, page: '1' });
              }}
            />
          );
        case 'plannedStartDate':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={createdTimeRangeOptions}
              value={filterPlannedStart || 'all'}
              placeholder="All Time"
              active={searchParams.has('plannedStart')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({
                  plannedStart: val && val !== 'all' ? val : null,
                  page: '1',
                });
              }}
            />
          );
        case 'plannedEndDate':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={createdTimeRangeOptions}
              value={filterPlannedEnd || 'all'}
              placeholder="All Time"
              active={searchParams.has('plannedEnd')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({
                  plannedEnd: val && val !== 'all' ? val : null,
                  page: '1',
                });
              }}
            />
          );
        case 'createdBy':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={userFilterOptions}
              value={filterCreatedBy}
              placeholder="All Users"
              onChange={(v) => {
                patchSearchParams({ createdBy: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterNumber,
      filterDescription,
      customerOptions,
      filterCustomer,
      engagementOptions,
      filterEngagement,
      filterPriority,
      filterState,
      assignedToOptions,
      assignedToColumnValue,
      handleAssignedToColumnFilter,
      assignmentGroupOptions,
      filterAssignmentGroup,
      createdTimeRangeOptions,
      filterTimeRange,
      filterPlannedStart,
      filterPlannedEnd,
      filterCreatedBy,
      userFilterOptions,
      searchParams,
      patchSearchParams,
    ]
  );

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: `"${appliedSearchTerm}"`,
        color: 'blue',
      });
    }

    if (filterNumber) {
      chips.push({ id: 'number', label: 'Number', value: filterNumber, color: 'blue' });
    }

    if (filterDescription) {
      chips.push({ id: 'desc', label: 'Description', value: filterDescription, color: 'blue' });
    }

    if (filterState.length > 0) {
      chips.push({
        id: 'state',
        label: 'State',
        value: filterState.map(s => stateLabels[s as keyof typeof stateLabels] || s).join(', '),
        color: 'purple',
      });
    }

    if (filterPriority.length > 0) {
      chips.push({
        id: 'priority',
        label: 'Priority',
        value: filterPriority.map(p => priorityLabels[p] || p).join(', '),
        color: 'orange',
      });
    }

    if (filterTimeRange) {
      let timeLabel = '';
      if (filterTimeRange.startsWith('custom:')) {
        const [, dates] = filterTimeRange.split('custom:');
        const [from, to] = dates.split(',');
        timeLabel = `${from} to ${to}`;
      } else {
        const timeOption = timeRangeOptions.find(t => t.value === filterTimeRange);
        timeLabel = timeOption?.label || filterTimeRange;
      }
      chips.push({
        id: 'timeRange',
        label: 'Created',
        value: timeLabel,
        color: 'orange',
      });
    }

    if (filterPlannedStart) {
      let timeLabel = '';
      if (filterPlannedStart.startsWith('custom:')) {
        const [, dates] = filterPlannedStart.split('custom:');
        const [from, to] = dates.split(',');
        timeLabel = `${from} to ${to}`;
      } else {
        const timeOption = timeRangeOptions.find(t => t.value === filterPlannedStart);
        timeLabel = timeOption?.label || filterPlannedStart;
      }
      chips.push({
        id: 'plannedStart',
        label: 'Planned Start',
        value: timeLabel,
        color: 'orange',
      });
    }

    if (filterPlannedEnd) {
      let timeLabel = '';
      if (filterPlannedEnd.startsWith('custom:')) {
        const [, dates] = filterPlannedEnd.split('custom:');
        const [from, to] = dates.split(',');
        timeLabel = `${from} to ${to}`;
      } else {
        const timeOption = timeRangeOptions.find(t => t.value === filterPlannedEnd);
        timeLabel = timeOption?.label || filterPlannedEnd;
      }
      chips.push({
        id: 'plannedEnd',
        label: 'Planned End',
        value: timeLabel,
        color: 'orange',
      });
    }

    if (filterCreatedBy) {
      const user = userFilterOptions.find((o) => o.value === filterCreatedBy);
      chips.push({
        id: 'createdBy',
        label: 'Created By',
        value: user?.label || filterCreatedBy,
        color: 'teal',
      });
    }

    if (filterCustomer) {
      const customer = customersData?.customers?.find((c: any) => c.id === filterCustomer);
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customer?.accountShortName || customer?.accountName || 'Unknown',
        color: 'teal',
      });
    }

    if (filterEngagement.length > 0) {
      const engagementNames = filterEngagement.map(engId => {
        const engagement = engagementsData?.engagements?.find((e: any) => e.id === engId);
        return engagement?.name || 'Unknown';
      });
      chips.push({
        id: 'engagement',
        label: 'Engagement',
        value: engagementNames.join(', '),
        color: 'cyan',
      });
    }

    if (appliedAlertNumber) {
      chips.push({
        id: 'alertNumber',
        label: 'Alert Number',
        value: appliedAlertNumber,
        color: 'red',
      });
    }

    if (filterPendingPeerReview) {
      chips.push({
        id: 'pendingPeerReview',
        label: 'Peer Review Pending',
        value: 'Yes',
        color: 'purple',
      });
    }

    if (filterPendingCAB) {
      chips.push({
        id: 'pendingCAB',
        label: 'CAB Approval Pending',
        value: 'Yes',
        color: 'orange',
      });
    }

    if (filterPendingCustomer) {
      chips.push({
        id: 'pendingCustomer',
        label: 'On Behalf of Customer',
        value: 'Yes',
        color: 'purple',
      });
    }

    if (filterAssignmentGroup.length > 0) {
      const groupNames = filterAssignmentGroup.map(groupId => {
        const group = assignmentGroupsData?.find((g: { id: string; name: string }) => g.id === groupId);
        return group?.name || 'Unknown';
      });
      chips.push({
        id: 'assignmentGroup',
        label: 'Assignment Group',
        value: groupNames.join(', '),
        color: 'purple',
      });
    }

    if (filterAssignedToMe) {
      chips.push({
        id: 'assignedToMe',
        label: 'Assigned To Me',
        value: filterAssignedToMe === 'true' ? 'Yes' : 'No',
        color: filterAssignedToMe === 'true' ? 'green' : 'gray',
      });
    }

    if (filterAssignedTo) {
      const assignedUser = userFilterOptions.find((o) => o.value === filterAssignedTo);
      chips.push({
        id: 'assignedTo',
        label: 'Assigned To',
        value: assignedUser?.label || 'Unknown',
        color: 'teal',
      });
    }

    if (filterUnassigned === 'true') {
      chips.push({
        id: 'unassigned',
        label: 'Show Unassigned',
        value: 'Yes',
        color: 'orange',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterDescription, filterState, filterPriority, filterTimeRange, filterPlannedStart, filterPlannedEnd, filterCreatedBy, filterCustomer, filterEngagement, appliedAlertNumber, filterPendingPeerReview, filterPendingCAB, filterPendingCustomer, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, customersData, engagementsData, assignmentGroupsData, userFilterOptions]);

  // Handle filter dropdown changes
  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'state': {
        const arr = value as string[];
        patchSearchParams({
          state: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'priority': {
        const arr = value as string[];
        patchSearchParams({
          priority: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'timeRange': {
        const v = value as string;
        const timeParam = serializeChangeTimeRangeForUrl(v);
        patchSearchParams({
          time: timeParam,
          page: '1',
        });
        break;
      }
      case 'customer':
        patchSearchParams({
          customer: (value as string) || null,
          engagement: null,
          page: '1',
        });
        break;
      case 'engagement': {
        const arr = value as string[];
        patchSearchParams({
          engagement: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'alertNumber':
        setFilterAlertNumber(value as string);
        break;
      case 'assignmentGroup': {
        const arr = value as string[];
        patchSearchParams({
          groups: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'assignedToMe':
        patchSearchParams({
          assignedToMe:
            value === 'true' || value === 'false' ? (value as string) : null,
          page: '1',
        });
        break;
      case 'assignedTo':
        patchSearchParams({
          assignedTo: (value as string) || null,
          page: '1',
        });
        break;
      case 'unassigned':
        patchSearchParams({
          unassigned: value === 'true' ? 'true' : null,
          page: '1',
        });
        break;
    }
  };

  // Handle applying alert number filter
  const handleApplyFilters = () => {
    if (filterAlertNumber !== appliedAlertNumber) {
      patchSearchParams({
        alert: filterAlertNumber.trim() || null,
        page: '1',
      });
    }
  };

  // Handle removing a single filter chip
  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchTerm('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'desc':
        patchSearchParams({ desc: null, page: '1' });
        break;
      case 'state':
        patchSearchParams({ state: null, page: '1' });
        break;
      case 'priority':
        patchSearchParams({ priority: null, page: '1' });
        break;
      case 'timeRange':
        patchSearchParams({ time: 'all', page: '1' });
        break;
      case 'plannedStart':
        patchSearchParams({ plannedStart: null, page: '1' });
        break;
      case 'plannedEnd':
        patchSearchParams({ plannedEnd: null, page: '1' });
        break;
      case 'createdBy':
        patchSearchParams({ createdBy: null, page: '1' });
        break;
      case 'customer':
        patchSearchParams({ customer: null, engagement: null, page: '1' });
        break;
      case 'engagement':
        patchSearchParams({ engagement: null, page: '1' });
        break;
      case 'alertNumber':
        setFilterAlertNumber('');
        patchSearchParams({ alert: null, page: '1' });
        break;
      case 'pendingPeerReview':
        patchSearchParams({ peer: null, page: '1' });
        break;
      case 'pendingCAB':
        patchSearchParams({ cab: null, page: '1' });
        break;
      case 'pendingCustomer':
        patchSearchParams({ cust: null, page: '1' });
        break;
      case 'assignmentGroup':
        patchSearchParams({ groups: null, page: '1' });
        break;
      case 'assignedToMe':
        patchSearchParams({ assignedToMe: null, page: '1' });
        break;
      case 'assignedTo':
        patchSearchParams({ assignedTo: null, page: '1' });
        break;
      case 'unassigned':
        patchSearchParams({ unassigned: null, page: '1' });
        break;
    }
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSearchTerm('');
    setFilterAlertNumber('');
    patchSearchParams({
      search: null,
      number: null,
      desc: null,
      state: null,
      priority: null,
      time: 'all',
      customer: null,
      engagement: null,
      alert: null,
      groups: null,
      assignedToMe: null,
      assignedTo: null,
      unassigned: null,
      peer: null,
      cab: null,
      cust: null,
      createdBy: null,
      plannedStart: null,
      plannedEnd: null,
      page: '1',
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['changes', page, pageSize, effectiveSearch, filterState, filterPriority, filterTimeRange, filterPlannedStart, filterPlannedEnd, filterCreatedBy, filterCustomer, filterEngagement, appliedAlertNumber, filterPendingPeerReview, filterPendingCAB, filterPendingCustomer, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, timezoneOffset],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('limit', pageSize.toString());
      params.append('offset', ((page - 1) * pageSize).toString());

      if (effectiveSearch) params.append('search', effectiveSearch);
      if (filterState.length > 0) params.append('changeState', filterState.join(','));
      if (filterPriority.length > 0) params.append('priority', filterPriority.join(','));

      // Handle time range - convert preset to date range
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          if (from) params.append('dateFrom', from);
          if (to) params.append('dateTo', to);
          params.append('timezoneOffset', String(timezoneOffset));
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          if (range.from) params.append('dateFrom', range.from);
          if (range.to) params.append('dateTo', range.to);
          params.append('timezoneOffset', String(timezoneOffset));
        } else {
          params.append('timeRange', filterTimeRange);
        }
      }

      if (filterCustomer) params.append('customerId', filterCustomer);
      if (filterEngagement.length > 0) params.append('engagementId', filterEngagement.join(','));
      if (appliedAlertNumber) params.append('alertNumber', appliedAlertNumber);
      // Three separate approval filter buckets
      if (filterPendingPeerReview) params.append('pendingPeerReview', 'true');
      if (filterPendingCAB) params.append('pendingCAB', 'true');
      if (filterPendingCustomer) params.append('pendingCustomer', 'true');
      if (filterAssignmentGroup.length > 0) params.append('assignmentGroupId', filterAssignmentGroup.join(','));
      if (filterAssignedToMe) params.append('assignedToMe', filterAssignedToMe);
      if (filterAssignedTo) params.append('assignedToId', filterAssignedTo);
      if (filterUnassigned) params.append('unassigned', filterUnassigned);
      if (filterCreatedBy) params.append('createdById', filterCreatedBy);
      appendChangeDateFilterParams(params, 'plannedStart', filterPlannedStart, timezoneOffset);
      appendChangeDateFilterParams(params, 'plannedEnd', filterPlannedEnd, timezoneOffset);

      const response = await api.get(`/changes?${params.toString()}`);
      return {
        changes: response.data.data || [],
        total: response.data.total || 0,
        totalPages: Math.ceil((response.data.total || 0) / 20)
      };
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 30000 // 30 seconds
  });

  // Log "Changes List Viewed" when data is loaded successfully
  useEffect(() => {
    if (data && user && !hasLoggedView.current && !isLoading) {
      hasLoggedView.current = true;
      trackChangeOperation(
        'Changes List',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Changes List Viewed',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [data, user, isLoading, isServiceDeskUser]);

  // Fetch change stats for the summary bar (with same filters as list)
  const { data: statsData } = useQuery({
    queryKey: ['change-stats', effectiveSearch, filterState, filterPriority, filterTimeRange, filterPlannedStart, filterPlannedEnd, filterCreatedBy, filterCustomer, filterEngagement, appliedAlertNumber, filterPendingPeerReview, filterPendingCAB, filterPendingCustomer, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, timezoneOffset],
    queryFn: async () => {
      // Build time range params (same as list query)
      let timeRangeParam = '';
      let dateFrom = '';
      let dateTo = '';
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          dateFrom = from;
          dateTo = to;
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          dateFrom = range.from;
          dateTo = range.to;
        } else {
          timeRangeParam = filterTimeRange;
        }
      }

      const params = new URLSearchParams({
        ticketType: 'CHANGE',
        ...(effectiveSearch && { search: effectiveSearch }),
        ...(filterState.length > 0 && { state: filterState.join(',') }),
        ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
        ...(timeRangeParam && { timeRange: timeRangeParam }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...((dateFrom || dateTo) && { timezoneOffset: String(timezoneOffset) }),
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
        ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
        ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
        ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
        ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
        ...(filterUnassigned && { unassigned: filterUnassigned }),
        ...(filterCreatedBy && { createdById: filterCreatedBy }),
      });

      if (filterPlannedStart) appendChangeDateFilterParams(params, 'plannedStart', filterPlannedStart, timezoneOffset);
      if (filterPlannedEnd) appendChangeDateFilterParams(params, 'plannedEnd', filterPlannedEnd, timezoneOffset);

      const response = await api.get(`/cases/stats?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000 // 30 seconds - match list query
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchTerm.trim() || null,
      page: '1',
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading changes. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Left: Title and Filter */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Changes</h1>
              <FilterDropdown
                sections={filterSections}
                values={{
                  state: filterState,
                  priority: filterPriority,
                  timeRange: filterTimeRange,
                  customer: filterCustomer,
                  engagement: filterEngagement,
                  alertNumber: filterAlertNumber,
                  assignmentGroup: filterAssignmentGroup,
                  assignedToMe: filterAssignedToMe,
                  assignedTo: filterAssignedTo,
                  unassigned: filterUnassigned,
                }}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
              />
              {/* Three separate approval filter buckets */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    patchSearchParams({
                      peer: filterPendingPeerReview ? null : '1',
                      page: '1',
                    });
                  }}
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    filterPendingPeerReview
                      ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400 hover:bg-yellow-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                  title={filterPendingPeerReview ? "Showing changes pending your peer review" : "Show changes pending your peer review"}
                >
                  {filterPendingPeerReview ? '✓ ' : ''}Peer Review
                </button>
                <button
                  type="button"
                  onClick={() => {
                    patchSearchParams({
                      cab: filterPendingCAB ? null : '1',
                      page: '1',
                    });
                  }}
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    filterPendingCAB
                      ? 'bg-orange-100 text-orange-800 border-2 border-orange-400 hover:bg-orange-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                  title={filterPendingCAB ? "Showing changes pending your CAB approval" : "Show changes pending your CAB approval"}
                >
                  {filterPendingCAB ? '✓ ' : ''}CAB Approval
                </button>
                <button
                  type="button"
                  onClick={() => {
                    patchSearchParams({
                      cust: filterPendingCustomer ? null : '1',
                      page: '1',
                    });
                  }}
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                    filterPendingCustomer
                      ? 'bg-purple-100 text-purple-800 border-2 border-purple-400 hover:bg-purple-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                  title={filterPendingCustomer ? "Showing changes pending customer approval (on your behalf)" : "Show changes pending customer approval (on your behalf)"}
                >
                  {filterPendingCustomer ? '✓ ' : ''}On Behalf of Customer
                </button>
              </div>
            </div>

            {/* Right: Search, New Button, and Settings */}
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search changes or numbers (comma/space separated)..."
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={!isTicketSearchInputValid(searchTerm)}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Search
              </button>
              <Link
                to="/changes/new"
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                New
              </Link>
              <ListLayoutSettingsButton onClick={() => setShowLayoutSettings(true)} />
              <RefreshButton
                queryKeys={['changes']}
                tooltip="Refresh changes"
                size="md"
                isLoading={isLoading}
              />
            </form>
          </div>

          {/* Active Filters - Inline */}
          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={handleClearAllFilters}
                onSaveAsDefault={handleSaveFilters}
                isSaving={isFilterSaving}
                hasUnsavedChanges={hasUnsavedFilterChanges}
              />
            </div>
          )}

          {/* Stats Summary Bar */}
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <span className="text-gray-600">
                Total: <span className="font-semibold text-gray-900">{statsData?.total?.toLocaleString() ?? data?.total?.toLocaleString() ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-600">Critical:</span>
                <span className="font-semibold text-gray-900">{statsData?.byPriority?.critical_1 ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                <span className="text-gray-600">High:</span>
                <span className="font-semibold text-gray-900">{statsData?.byPriority?.high_2 ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">Implement:</span>
                <span className="font-semibold text-gray-900">{statsData?.byState?.implement ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span className="text-gray-600">Scheduled:</span>
                <span className="font-semibold text-gray-900">{statsData?.byState?.scheduled ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-600">Unassigned:</span>
                <span className="font-semibold text-gray-900">{statsData?.unassigned ?? '-'}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Export CSV Button */}
              {data?.changes && data.changes.length > 0 && (
                <GenericExportButton
                  config={ExportConfigs.CHANGES}
                  filters={exportFilters}
                  columns={exportColumns}
                  totalRows={data?.total}
                  disabled={!data?.changes || data.changes.length === 0}
                  onExportStart={() => {
                    if (user) {
                      trackChangeOperation(
                        'Changes List',
                        user.email || user.firstName || 'Unknown',
                        {
                          action: 'Export Button Clicked',
                        },
                        {
                          firstName: user.firstName,
                          lastName: user.lastName,
                          role: user.role,
                          roleType: user.roleType,
                          roleLevel: user.roleLevel,
                          userClass: user.class,
                          customerId: user.customerId,
                          customerName: user.customerName,
                          engagementId: user.engagementId,
                          engagementName: user.engagementName,
                          isServiceDeskUser: isServiceDeskUser,
                        }
                      );
                    }
                  }}
                />
              )}
              {(statsData?.slaBreached ?? 0) > 0 && (
                <span className="flex items-center gap-1.5 text-red-600">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <span>SLA Breached:</span>
                  <span className="font-semibold">{statsData?.slaBreached}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Changes Table - Takes remaining space */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader(col.key, col.label)}
                </th>
              ))}
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.changes?.map((change: Change) => {
              // Column renderer function
              const renderColumn = (columnKey: string) => {
                switch (columnKey) {
                  case 'number':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/changes/${change.id || change._id}`}
                            state={{ changesListSearch: location.search }}
                            className="text-indigo-600 hover:text-indigo-900 hover:underline"
                            title="View change details"
                          >
                            {change.number}
                          </Link>
                          {change.relatedRecordsCount !== undefined && change.relatedRecordsCount > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200"
                              title={`${change.relatedRecordsCount} related record${change.relatedRecordsCount > 1 ? 's' : ''}`}
                            >
                              {change.relatedRecordsCount}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  case 'shortDescription':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{stripHtml(change.shortDescription)}</div>
                      </td>
                    );
                  case 'state':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <div className="flex items-center">
                          {stateIcons[(change.changeState || change.state) as keyof typeof stateIcons]}
                          <span className="ml-2 text-gray-900">
                            {stateLabels[(change.changeState || change.state) as keyof typeof stateLabels]}
                          </span>
                        </div>
                      </td>
                    );
                  case 'priority':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[change.priority as keyof typeof priorityColors]}`}>
                          {priorityLabels[change.priority as keyof typeof priorityLabels]}
                        </span>
                      </td>
                    );
                  case 'plannedDate':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          if (change.plannedStartDate && change.plannedEndDate) {
                            const startDate = new Date(change.plannedStartDate).toLocaleDateString();
                            const endDate = new Date(change.plannedEndDate).toLocaleDateString();
                            return (
                              <div className="text-xs">
                                <div>{startDate}</div>
                                <div className="text-gray-500">to {endDate}</div>
                              </div>
                            );
                          } else if (change.plannedStartDate) {
                            return <div className="text-xs">{new Date(change.plannedStartDate).toLocaleDateString()}</div>;
                          } else {
                            return <span className="text-gray-500">Not Scheduled</span>;
                          }
                        })()}
                      </td>
                    );
                  case 'customer':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {change.Customer ?
                          <span title={change.Customer.accountName}>
                            {change.Customer.accountShortName || change.Customer.accountName}
                          </span> :
                          <span className="text-gray-500">No Customer</span>
                        }
                      </td>
                    );
                  case 'engagement':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {change.Engagement ?
                          <span title={change.Engagement.name}>
                            {change.Engagement.name}
                          </span> :
                          <span className="text-gray-500">No Engagement</span>
                        }
                      </td>
                    );
                  case 'assignmentGroup':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {change.Group_Case_assignmentGroupIdToGroup ?
                          <span title={change.Group_Case_assignmentGroupIdToGroup.description}>
                            {change.Group_Case_assignmentGroupIdToGroup.name}
                          </span> :
                          <span className="text-gray-500">Unassigned</span>
                        }
                      </td>
                    );
                  case 'assignedTo':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {change.User_Case_assignedToIdToUser ? (
                          `${change.User_Case_assignedToIdToUser.firstName || ''} ${change.User_Case_assignedToIdToUser.lastName || ''}`.trim() ||
                          change.User_Case_assignedToIdToUser.email ||
                          <span className="text-gray-500">Unassigned</span>
                        ) : (
                          <span className="text-gray-500">Unassigned</span>
                        )}
                      </td>
                    );
                  case 'recentActions':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        {(() => {
                          const recentActions = getRecentActions(change.CaseActivity);
                          if (recentActions.length === 0) {
                            return <span className="text-gray-500">No recent actions</span>;
                          }

                          return (
                            <div className="space-y-1">
                              {recentActions.map((activity) => {
                                const formattedAction = formatActivityAction(activity);
                                return (
                                  <div key={activity.id} className="flex flex-col">
                                    <span className={`${formattedAction.color} font-medium`}>
                                      {formattedAction.text}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {formattedAction.date}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </td>
                    );
                  case 'changeType':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(change as any).changeType ? (change as any).changeType.replace('_', ' ') : '-'}
                      </td>
                    );
                  case 'riskLevel':
                    // Database column is 'risk', not 'riskLevel'
                    const riskValue = (change as any).risk;
                    let riskText = '-';
                    let riskColor = 'text-gray-500';
                    if (riskValue === 'low') {
                      riskText = 'Low';
                      riskColor = 'text-green-600';
                    } else if (riskValue === 'medium') {
                      riskText = 'Medium';
                      riskColor = 'text-yellow-600';
                    } else if (riskValue === 'high') {
                      riskText = 'High';
                      riskColor = 'text-red-600';
                    } else if (riskValue) {
                      riskText = riskValue.charAt(0).toUpperCase() + riskValue.slice(1);
                    }
                    return (
                      <td key={columnKey} className={`px-3 py-2 whitespace-nowrap text-sm ${riskColor}`}>
                        {riskText}
                      </td>
                    );
                  case 'plannedStartDate':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {((change as any).plannedStart || change.plannedStartDate) ? (
                          <div className="flex flex-col">
                            <span>{new Date((change as any).plannedStart || change.plannedStartDate).toLocaleDateString()}</span>
                            <span className="text-xs">{new Date((change as any).plannedStart || change.plannedStartDate).toLocaleTimeString()}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'plannedEndDate':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {((change as any).plannedEnd || change.plannedEndDate) ? (
                          <div className="flex flex-col">
                            <span>{new Date((change as any).plannedEnd || change.plannedEndDate).toLocaleDateString()}</span>
                            <span className="text-xs">{new Date((change as any).plannedEnd || change.plannedEndDate).toLocaleTimeString()}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'actualStartDate':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {(change as any).actualStart ? new Date((change as any).actualStart).toLocaleDateString() : '-'}
                      </td>
                    );
                  case 'actualEndDate':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {(change as any).actualEnd ? new Date((change as any).actualEnd).toLocaleDateString() : '-'}
                      </td>
                    );
                  case 'approvalStatus':
                    // CAB Approval Status is derived from approvalStage:
                    // - 'customer' or 'complete' means CAB has approved
                    // - 'cab' means CAB approval is pending/in progress
                    // - 'peer_review' or null means not yet at CAB stage
                    const approvalStage = (change as any).approvalStage;
                    let cabApprovalStatus = '-';
                    let cabStatusColor = 'text-gray-500';
                    if (approvalStage === 'customer' || approvalStage === 'complete') {
                      cabApprovalStatus = 'Approved';
                      cabStatusColor = 'text-green-600';
                    } else if (approvalStage === 'cab') {
                      cabApprovalStatus = 'Pending';
                      cabStatusColor = 'text-yellow-600';
                    } else if (approvalStage === 'peer_review') {
                      cabApprovalStatus = 'Not Started';
                      cabStatusColor = 'text-gray-500';
                    }
                    return (
                      <td key={columnKey} className={`px-3 py-2 whitespace-nowrap text-sm ${cabStatusColor}`}>
                        {cabApprovalStatus}
                      </td>
                    );
                  case 'implementationPlan':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).implementationPlan ? stripHtml((change as any).implementationPlan) : '-'}</div>
                      </td>
                    );
                  case 'backoutPlan':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).backoutPlan ? stripHtml((change as any).backoutPlan) : '-'}</div>
                      </td>
                    );
                  case 'createdBy':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(change as any).createdBy?.firstName ? `${(change as any).createdBy.firstName} ${(change as any).createdBy.lastName || ''}` : '-'}
                      </td>
                    );
                  case 'createdAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {change.createdAt ? (
                          <div className="flex flex-col">
                            <span>{new Date(change.createdAt).toLocaleDateString()}</span>
                            <span className="text-xs">{new Date(change.createdAt).toLocaleTimeString()}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'impact':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(change as any).impact ? (change as any).impact.replace('_', ' ') : '-'}
                      </td>
                    );
                  case 'urgency':
                    const urgencyValue = (change as any).urgency;
                    let urgencyText = '-';
                    let urgencyColor = 'text-gray-500';
                    if (urgencyValue?.startsWith('low')) {
                      urgencyText = 'Low';
                      urgencyColor = 'text-green-600';
                    } else if (urgencyValue?.startsWith('medium')) {
                      urgencyText = 'Medium';
                      urgencyColor = 'text-yellow-600';
                    } else if (urgencyValue?.startsWith('high')) {
                      urgencyText = 'High';
                      urgencyColor = 'text-red-600';
                    } else if (urgencyValue) {
                      urgencyText = urgencyValue.replace('_', ' ');
                    }
                    return (
                      <td key={columnKey} className={`px-3 py-2 whitespace-nowrap text-sm ${urgencyColor}`}>
                        {urgencyText}
                      </td>
                    );
                  case 'category':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {change.category || '-'}
                      </td>
                    );
                  case 'justification':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).justification ? stripHtml((change as any).justification) : '-'}</div>
                      </td>
                    );
                  case 'riskImpactAnalysis':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).riskImpactAnalysis ? stripHtml((change as any).riskImpactAnalysis) : '-'}</div>
                      </td>
                    );
                  case 'testPlan':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).testPlan ? stripHtml((change as any).testPlan) : '-'}</div>
                      </td>
                    );
                  case 'implementationResults':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).implementationResults ? stripHtml((change as any).implementationResults) : '-'}</div>
                      </td>
                    );
                  case 'reviewResults':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).reviewResults ? stripHtml((change as any).reviewResults) : '-'}</div>
                      </td>
                    );
                  case 'maintenanceWindow':
                    // Calculate maintenance window from planned dates if not explicitly set
                    const maintenanceWindowText = (change as any).maintenanceWindow
                      ? stripHtml((change as any).maintenanceWindow)
                      : ((change as any).plannedStart && (change as any).plannedEnd)
                        ? `${new Date((change as any).plannedStart).toLocaleString()} - ${new Date((change as any).plannedEnd).toLocaleString()}`
                        : '-';
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{maintenanceWindowText}</div>
                      </td>
                    );
                  case 'customerApprovalStatus':
                    const customerStatus = (change as any).customerApprovalStatus;
                    let customerApprovalText = '-';
                    let customerStatusColor = 'text-gray-500';
                    if (customerStatus === 'approved') {
                      customerApprovalText = 'Approved';
                      customerStatusColor = 'text-green-600';
                    } else if (customerStatus === 'rejected') {
                      customerApprovalText = 'Rejected';
                      customerStatusColor = 'text-red-600';
                    } else if (customerStatus === 'pending') {
                      customerApprovalText = 'Pending';
                      customerStatusColor = 'text-yellow-600';
                    }
                    return (
                      <td key={columnKey} className={`px-3 py-2 whitespace-nowrap text-sm ${customerStatusColor}`}>
                        {customerApprovalText}
                      </td>
                    );
                  case 'changeCloseCode':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(change as any).changeCloseCode ? (change as any).changeCloseCode.replace('_', ' ') : '-'}
                      </td>
                    );
                  case 'slaPlan':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(change as any).ServicePlan?.name || '-'}
                      </td>
                    );
                  case 'slaBreached':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        {(change as any).slaBreached ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Breached
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            On Track
                          </span>
                        )}
                      </td>
                    );
                  case 'closedAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {(change as any).closedAt ? (
                          <div className="flex flex-col">
                            <span>{new Date((change as any).closedAt).toLocaleDateString()}</span>
                            <span className="text-xs">{new Date((change as any).closedAt).toLocaleTimeString()}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'closureNotes':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(change as any).closureNotes ? stripHtml((change as any).closureNotes) : '-'}</div>
                      </td>
                    );
                  default:
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        -
                      </td>
                    );
                }
              };

              return (
              <tr key={change.id || change._id} className="hover:bg-gray-50">
                {visibleColumns.map((col) => renderColumn(col.key))}
                <td className="px-3 py-2 whitespace-nowrap text-sm">
                  <button
                    onClick={() => openEmailModal(change)}
                    className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                    title="Email ticket details"
                  >
                    <EnvelopeIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {(!data?.changes || data.changes.length === 0) && (
          <div className="text-center py-12">
            <p className="text-gray-500">No changes found</p>
          </div>
        )}

      </div>

      {/* Pagination Controls - Always visible */}
      <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 bg-white px-3 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
        {data?.total > 0 ? (
          <>
          {/* Left: Records per page dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                patchSearchParams({
                  pageSize: e.target.value,
                  page: '1',
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size, index) => {
                const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                const isDisabled = previousSize >= (data?.total ?? 0) && size > (data?.total ?? 0);
                return (
                  <option key={size} value={size} disabled={isDisabled}>
                    {size}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {/* Center: Entry count */}
          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data?.total ?? 0)} to {Math.min(page * pageSize, data?.total ?? 0)} of {data?.total ?? 0} entries
          </div>

          {/* Right: Pagination buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil((data?.total ?? 0) / pageSize);
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchSearchParams({ page: String(p) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              onClick={() => patchSearchParams({ page: String(page + 1) })}
              disabled={page >= Math.ceil((data?.total ?? 0) / pageSize)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() =>
                patchSearchParams({
                  page: String(Math.ceil((data?.total ?? 0) / pageSize)),
                })
              }
              disabled={page >= Math.ceil((data?.total ?? 0) / pageSize)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
          </>
        ) : (
          <div className="text-sm text-gray-500 w-full text-center">No records</div>
        )}
      </div>

      {/* Email Ticket Modal */}
      <EmailTicketModal
        isOpen={emailModalState.isOpen}
        onClose={() => setEmailModalState({ isOpen: false, ticketId: '', ticketNumber: '', shortDescription: '' })}
        ticketId={emailModalState.ticketId}
        ticketType="CHANGE"
        ticketNumber={emailModalState.ticketNumber}
        shortDescription={emailModalState.shortDescription}
      />

      {/* List Layout Settings Modal */}
      <ListLayoutSettingsModal
        isOpen={showLayoutSettings}
        onClose={() => setShowLayoutSettings(false)}
        entityType="CHANGE"
        columns={layoutColumns}
        onSave={saveColumns}
        onReset={resetToDefault}
        isSaving={isLayoutSaving}
        isResetting={isLayoutResetting}
      />

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div
            className={`rounded-lg shadow-lg px-3 py-2 max-w-md ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : toast.type === 'error'
                ? 'bg-red-50 border border-red-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {toast.type === 'success' && (
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
              )}
              {toast.type === 'error' && (
                <XMarkIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
              {toast.type === 'warning' && (
                <QuestionMarkCircleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    toast.type === 'success'
                      ? 'text-green-800'
                      : toast.type === 'error'
                      ? 'text-red-800'
                      : 'text-yellow-800'
                  }`}
                >
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToast(null)}
                className={`flex-shrink-0 ${
                  toast.type === 'success'
                    ? 'text-green-400 hover:text-green-600'
                    : toast.type === 'error'
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-yellow-400 hover:text-yellow-600'
                }`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeList; 